import type { components } from '@connected/api-client/api.generated';
import type { ColumnDef } from '@tanstack/react-table';

import { portaalQueryOptions } from '@connected/portaal-sdk/queries/options';
import { Button } from '@connected/ui/components/button';
import { Separator } from '@connected/ui/components/separator';
import { Skeleton } from '@connected/ui/components/skeleton';
import { Spinner } from '@connected/ui/components/spinner';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { ChevronDown, ChevronRight, CloudDownload, RefreshCw } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { z } from 'zod';

import { ComingSoonModal } from '@/components/coming-soon-modal';
import { DataTable } from '@/components/data-table';
import { ExpandedRowsMobile } from '@/components/expanded-row';
import SearchInput from '@/components/search-input';
import { queryClient } from '@/lib/query-client';
import { formatFileSize } from '@/lib/utils';

export const Route = createFileRoute(
    '/_public/customer/locations/$locationId/electrical/projects/$projectId/_overview/overview/',
)({
    component: RouteComponent,
    validateSearch: z.object({
        filesView: z.enum(['rapports', 'inspections', 'offers', 'technical_drawings']).optional().default('rapports'),
        search: z.string().optional(),
    }),
});

export type ProjectObservations = components['schemas']['ViewElectricalProjectPortalQueryResultObservationGroup'];

export type ProjectTechnicaldrawings = components['schemas']['ListElectricalProjectTechnicalDrawingPortalQueryResultDrawing'] & {
    DownloadId: string;
};

// type ProjectSeverityList = ProjectObservations['RemarkElectricalProjectObservation'] | ProjectObservations['MinorElectricalProjectObservation'] | ProjectObservations['SeriousElectricalProjectObservation'] | ProjectObservations['SevereElectricalProjectObservation'];

type ListProjectObservationsV2 = {
    Key: ProjectObservations['GroupKind'];
    Value: string | null;
    ObservationRoute: string;
    Params: {
        ParamId: string;
        GroupId: ProjectObservations['NoneElectricalProjectObservation'];
    }[];
    DefaultRoute: string;
};

type ListDocumentsQuery = components['schemas']['ListElectricalProjectDocumentPortalQueryResultDocument'] & {
    DownloadId: string;
};

function RouteComponent() {
    const { t } = useTranslation('customer-module-electrical');
    const params = Route.useParams();
    const [comingSoonModalOpen, setComingSoonModalOpen] = useState<boolean>(false);
    const navigate = useNavigate();

    const projectQuery = useQuery(portaalQueryOptions.electrical.project.view({
        ElectricalProjectId: params.projectId ?? null,
    }));

    const colors = projectQuery?.data?.Project?.Codes?.filter(row => ({
        kind: row.Kind,
        hex: row.HexColorCode,
    }));

    const containsScope12Inspection = useMemo(() => {
        if (projectQuery?.isFetched && projectQuery.isSuccess && projectQuery?.data?.Project?.Templates && projectQuery?.data?.Project?.Templates.length > 0) {
            return projectQuery?.data?.Project?.Templates?.some(row => row?.InspectionKind === 'Scope12');
        }
        return false;
    }, [projectQuery?.data?.Project?.Templates, projectQuery?.isFetched, projectQuery.isSuccess]);

    const listedRoutes = useMemo<ListProjectObservationsV2[]>(() => [
        {
            Key: 'DistributionBoard',
            Value: t('module-electrical.overview.observations-table.rows.DistributionBoard'),
            DefaultRoute: '/customer/locations/$locationId/electrical/projects/$projectId/distribution-boards/',
            ObservationRoute: '/customer/locations/$locationId/electrical/projects/$projectId/distribution-boards/$distributionboardId/observations/$observationId',
            Params: [
                {
                    ParamId: '$distributionboardId',
                    GroupId: { ElectricalProjectDistributionBoardId: '' },
                },
                {
                    ParamId: '$observationId',
                    GroupId: { ElectricalProjectObservationId: '' },
                },
            ],
        },
        {
            Key: 'FinalCircuit',
            Value: t('module-electrical.overview.observations-table.rows.FinalCircuit'),
            DefaultRoute: '/customer/locations/$locationId/electrical/projects/$projectId/final-circuit/',
            ObservationRoute: '/customer/locations/$locationId/electrical/projects/$projectId/final-circuit/$controlboxId/observations/$observationId',
            Params: [
                {
                    ParamId: '$controlboxId',
                    GroupId: { ElectricalProjectFinalCircuitId: '' },
                },
                {
                    ParamId: '$observationId',
                    GroupId: { ElectricalProjectObservationId: '' },
                },
            ],
        },
        ...(containsScope12Inspection
            ? [
                    {
                        Key: 'PVInverter' as ProjectObservations['GroupKind'],
                        Value: t('module-electrical.overview.observations-table.rows.PhotovoltaicInverter'),
                        DefaultRoute: '/customer/locations/$locationId/electrical/projects/$projectId/pv-installations',
                        ObservationRoute: '/customer/locations/$locationId/electrical/projects/$projectId/pv-installations/$pvInverterId/observations/$observationId',
                        Params: [
                            {
                                ParamId: '$pvInverterId',
                                GroupId: { ElectricalProjectPVInverterId: '' },
                            },
                            {
                                ParamId: '$observationId',
                                GroupId: { ElectricalProjectObservationId: '' },
                            },
                        ],
                    },
                ]
            : []),
    ], [containsScope12Inspection, t]);

    // const correctRouteSet = useMemo(() => {
    //     return (row: ProjectObservations, severity: ProjectSeverityList | null | undefined) => {
    //         const routeConfig = listedRoutes.find(r => r.Key === row.GroupKind);

    //         const route = routeConfig?.DefaultRoute ?? '';

    //         if (!severity)
    //             return route;

    //         if (severity) {
    //             if (!routeConfig || !routeConfig.Params || !routeConfig.ObservationRoute) {
    //                 return route;
    //             }

    //             let builtRoute = routeConfig.ObservationRoute;

    //             routeConfig.Params.forEach((param) => {
    //                 const groupKey = Object.keys(param.GroupId ?? {})[0] as keyof ProjectSeverityList;
    //                 const value = severity[groupKey];

    //                 if (value) {
    //                     builtRoute = builtRoute.replace(param.ParamId, value);
    //                 }
    //             });

    //             return builtRoute;
    //         }

    //         return route;
    //     };
    // }, [listedRoutes]);

    const columnsNew = useMemo<ColumnDef<ProjectObservations>[]>(() => ([
        {
            accessorKey: 'ElectricalProjectPVStringId',
            meta: {
                sticky: true,
                width: 180,
            },
            header: () => (
                <DataTable.Header>
                    {t('module-electrical.distribution-boards.list.name')}
                </DataTable.Header>
            ),
            cell: ({ row }: { row: { original: ProjectObservations } }) => {
                return (
                    <div className="flex flex-col">
                        <DataTable.Cell variant="link">
                            <div onClick={() => {
                                navigate({
                                    to: listedRoutes.find(route => route.Key === row?.original?.GroupKind)?.DefaultRoute,
                                });
                            }}
                            >
                                {listedRoutes.find(route => route.Key === row?.original?.GroupKind)?.Value}
                            </div>
                        </DataTable.Cell>
                        <div className="text-text-primary/60 font-normal">
                            {`${row?.original?.ObjectCount} ${listedRoutes.find(route => route.Key === row?.original?.GroupKind)?.Value?.toLocaleLowerCase()}`}
                        </div>
                    </div>
                );
            },
        },
        {
            accessorKey: 'SevereObservationCount',
            header: () => (
                <DataTable.Header>
                    {t('module-electrical.distribution-boards.list.severe')}
                </DataTable.Header>
            ),
            cell: ({ row }: { row: { original: ProjectObservations } }) => {
                if ((row.original?.SevereObservationCount ?? 0) < 1) {
                    return null;
                }
                return (
                    <div
                        className="flex"
                        // onClick={() => {
                        //     navigate({
                        //         to: correctRouteSet(row.original, row.original.SevereElectricalProjectObservation),
                        //     });
                        // }}
                    >
                        <DataTable.Cell
                            variant="observation"
                            props={{
                                label: t('module-electrical.distribution-boards.list.severe'),
                                color: colors?.find(c => c.Kind === 'Severe')?.HexColorCode ?? '#000000',
                                children: row?.original?.SevereObservationCount,
                            }}
                            label={t('module-electrical.distribution-boards.list.severe')}
                        />
                    </div>
                );
            },
        },
        {
            accessorKey: 'SeriousObservationCount',
            header: () => (
                <DataTable.Header>
                    {t('module-electrical.distribution-boards.list.serious')}
                </DataTable.Header>
            ),
            cell: ({ row }: { row: { original: ProjectObservations } }) => {
                if ((row.original?.SeriousObservationCount ?? 0) < 1) {
                    return null;
                }
                return (
                    <div
                        className="flex"
                        // onClick={() => {
                        //     navigate({
                        //         to: correctRouteSet(row.original, row.original.SeriousElectricalProjectObservation),
                        //     });
                        // }}
                    >
                        <DataTable.Cell
                            variant="observation"
                            props={{
                                label: t('module-electrical.distribution-boards.list.serious'),
                                color: colors?.find(c => c.Kind === 'Serious')?.HexColorCode ?? '#000000',
                                children: row?.original?.SeriousObservationCount,
                            }}
                            label={t('module-electrical.distribution-boards.list.serious')}
                        />
                    </div>
                );
            },
        },
        {
            accessorKey: 'MinorObservationCount',
            header: () => (
                <DataTable.Header>
                    {t('module-electrical.distribution-boards.list.minor')}
                </DataTable.Header>
            ),
            cell: ({ row }: { row: { original: ProjectObservations } }) => {
                if ((row.original?.MinorObservationCount ?? 0) < 1) {
                    return null;
                }
                return (
                    <div
                        className="flex items-center min-h-8"
                        // onClick={() => {
                        //     navigate({
                        //         to: correctRouteSet(row.original, row.original.MinorElectricalProjectObservation),
                        //     });
                        // }}
                    >
                        <DataTable.Cell
                            variant="observation"
                            props={{
                                label: t('module-electrical.distribution-boards.list.minor'),
                                color: colors?.find(c => c.Kind === 'Minor')?.HexColorCode ?? '#000000',
                                children: row?.original?.MinorObservationCount,
                            }}
                            label={t('module-electrical.distribution-boards.list.minor')}
                        />
                    </div>
                );
            },
        },
        {
            accessorKey: 'RemarkObservationCount',
            header: () => (
                <DataTable.Header>
                    {t('module-electrical.distribution-boards.list.remarks')}
                </DataTable.Header>
            ),
            cell: ({ row }: { row: { original: ProjectObservations } }) => {
                if ((row.original?.RemarkObservationCount ?? 0) < 1) {
                    return null;
                }
                return (
                    <div
                        className="flex"
                        // onClick={() => {
                        //     navigate({
                        //         to: correctRouteSet(row.original, row.original.RemarkElectricalProjectObservation),
                        //     });
                        // }}
                    >
                        <DataTable.Cell
                            variant="observation"
                            props={{
                                label: t('module-electrical.distribution-boards.list.remarks'),
                                color: colors?.find(c => c.Kind === 'Remark')?.HexColorCode ?? '#000000',
                                children: row?.original?.RemarkObservationCount,
                            }}
                            label={t('module-electrical.distribution-boards.list.remarks')}
                        />
                    </div>
                );
            },
        },

    ]), [colors, listedRoutes, navigate, t]);

    return (
        <>
            <DataTable.TableHead
                title={t(
                    'module-electrical.overview.observations-table.header.title',
                )}
                subtitle={t(
                    'module-electrical.overview.observations-table.header.subtitle',
                )}
            />
            <DataTable
                columns={columnsNew}
                data={projectQuery.data?.Project?.ObservationsV2?.filter(row => listedRoutes.some((x: ListProjectObservationsV2) => x.Key === row.GroupKind)) ?? []}
            />
            <div className="flex sm:flex-row flex-col justify-between w-full gap-4 mb-8">
                <div className="border rounded-lg p-6 flex-1">
                    <div className="flex flex-row items-center justify-between">
                        <h2 className="text-lg font-semibold">
                            {t(
                                'module-electrical.overview.installation-details.title',
                            )}
                        </h2>
                    </div>
                    <div className="mt-4">
                        <p className="text-sm font-semibold mb-1">
                            {t(
                                'module-electrical.overview.installation-details.system',
                            )}
                        </p>
                        <p className="text-sm">
                            {projectQuery.data?.Project?.ElectricalEarthingSystemKinds?.join(', ') || '-'}
                        </p>
                    </div>
                    <Separator className="my-4 bg-background" />
                    <div>
                        <p className="text-sm font-semibold mb-1">
                            {t(
                                'module-electrical.overview.installation-details.details',
                            )}
                        </p>
                        <p className="text-sm">
                            {projectQuery.data?.Project?.Remarks?.trim() || '-'}
                        </p>
                    </div>
                    <Separator className="my-4 bg-background" />
                    <div>
                        <p className="text-sm font-semibold mb-1">
                            {t(
                                'module-electrical.overview.installation-details.construction-installation',
                            )}
                        </p>
                        <p className="text-sm">
                            {projectQuery.data?.Project?.ElectricalInstallationConstructionYear || '-'}
                        </p>
                    </div>
                    <Separator className="my-4 bg-background" />
                    <div>
                        <p className="text-sm font-semibold mb-1">
                            {t(
                                'module-electrical.overview.installation-details.special-spaces',
                            )}
                        </p>
                        <div className="text-sm">
                            {
                                projectQuery.data?.Project?.SpecialSpaces?.length
                                    ? (
                                            <ul className="ps-5">
                                                {projectQuery.data?.Project.SpecialSpaces.map(func => (
                                                    <li className="list-disc" key={String(func)}>{func}</li>
                                                ))}
                                            </ul>
                                        )
                                    : '-'
                            }
                        </div>
                    </div>
                    <Separator className="my-4 bg-background" />
                    <div>
                        <p className="text-sm font-semibold mb-1">
                            {t(
                                'module-electrical.overview.installation-details.inspection-interval',
                            )}
                        </p>
                        <p className="text-sm">
                            {projectQuery.data?.Project?.NextInspectionInYearCount
                                ? `${projectQuery.data?.Project.NextInspectionInYearCount} ${t(`module-electrical.overview.installation-details.years`)}`
                                : '-'}
                        </p>
                    </div>
                    <Separator className="my-4 bg-background" />
                    <div>
                        <p className="text-sm font-semibold mb-1">
                            {t(
                                'module-electrical.overview.installation-details.last-inspection-date',
                            )}
                        </p>
                        <p className="text-sm">
                            {projectQuery.data?.Project?.NextInspectionDate
                                ? new Date(projectQuery.data?.Project?.NextInspectionDate).toLocaleDateString('nl-NL', {
                                        day: '2-digit',
                                        month: '2-digit',
                                        year: 'numeric',
                                    })
                                : '-'}
                        </p>
                    </div>
                    <Separator className="my-4 bg-background" />
                    <div>
                        <p className="text-sm font-semibold mb-1">
                            {t(
                                'module-electrical.overview.installation-details.usage-function',
                            )}
                        </p>
                        <div className="text-sm">
                            {
                                projectQuery.data?.Project?.BuildingFunctions?.length
                                    ? (
                                            <ul className="ps-5">
                                                {projectQuery.data?.Project.BuildingFunctions.filter(Boolean).map(func => (
                                                    <li className="list-disc" key={String(func)}>{func}</li>
                                                ))}
                                            </ul>
                                        )
                                    : '-'
                            }
                        </div>
                    </div>
                </div>
                <div className="border rounded-lg p-6 flex-1">
                    <div className="flex flex-row items-center justify-between">
                        <h2 className="text-lg font-semibold">
                            {t(
                                'module-electrical.overview.maintenance-company.title',
                            )}
                        </h2>
                        {/* TODO: remove hardcoded image with hardcoded strings */}
                        {/* <img className="h-6 object-fill" src="/logo-spie.svg" /> */}
                    </div>
                    <div className="mt-4">
                        <p className="text-sm font-semibold mb-1">
                            {t(
                                'module-electrical.overview.maintenance-company.name',
                            )}
                        </p>
                        <p className="text-sm">
                            {projectQuery.data?.Project?.Customer?.Name || '-'}
                        </p>
                    </div>
                    <Separator className="my-4 bg-background" />
                    <div>
                        <p className="text-sm font-semibold mb-1">
                            {t(
                                'module-electrical.overview.maintenance-company.address',
                            )}
                        </p>
                        <p className="text-sm">
                            {projectQuery.data?.Project?.Customer?.Address?.Street || projectQuery.data?.Project?.Customer?.Address?.StreetNumber
                                ? `${projectQuery.data?.Project?.Customer?.Address?.Street} ${projectQuery.data?.Project?.Customer?.Address?.StreetNumber}`
                                : '-'}
                        </p>
                    </div>
                    <Separator className="my-4 bg-background" />
                    <div>
                        <p className="text-sm font-semibold mb-1">
                            {t(
                                'module-electrical.overview.maintenance-company.place',
                            )}
                        </p>
                        <p className="text-sm">
                            {projectQuery.data?.Project?.Customer?.Address?.PostalCode || projectQuery.data?.Project?.Customer?.Address?.Locality
                                ? `${projectQuery.data?.Project?.Customer?.Address?.PostalCode} ${projectQuery.data?.Project?.Customer?.Address?.Locality}`
                                : '-'}
                        </p>
                    </div>
                    <Separator className="my-4 bg-background" />
                    <div>
                        <p className="text-sm font-semibold mb-1">
                            {t(
                                'module-electrical.overview.maintenance-company.contact-person',
                            )}
                        </p>
                        <p className="text-sm">
                            {projectQuery.data?.Project?.Customer?.ContactPersons?.[0]
                                ? `${projectQuery.data?.Project.Customer.ContactPersons[0].FirstName} ${projectQuery.data?.Project.Customer.ContactPersons[0].MiddleName} ${projectQuery.data?.Project.Customer.ContactPersons[0].LastName}`.replace(/\s+/g, ` `).trim()
                                : '-'}
                        </p>
                    </div>
                    <Separator className="my-4 bg-background" />
                    <div>
                        <p className="text-sm font-semibold mb-1">
                            {t(
                                'module-electrical.overview.maintenance-company.email',
                            )}
                        </p>
                        <p className="text-sm">
                            {projectQuery.data?.Project?.Customer?.Email || '-'}
                        </p>
                    </div>
                    <Separator className="my-4 bg-background" />
                    <div>
                        <p className="text-sm font-semibold mb-1">
                            {t(
                                'module-electrical.overview.maintenance-company.installation-manager',
                            )}
                        </p>
                        <div className="text-sm space-y-3">
                            {projectQuery.data?.Project?.ContactPersonsResponsibleForPowerSystem && projectQuery.data?.Project?.ContactPersonsResponsibleForPowerSystem.length > 0
                                ? projectQuery.data.Project.ContactPersonsResponsibleForPowerSystem.map(person => (
                                        <div key={person.ElectricalProjectContactPersonId}>
                                            <p>
                                                {`${person.FirstName || ''} ${person.MiddleName || ''} ${person.LastName || ''}`.replace(/\s+/g, ' ').trim()}
                                            </p>
                                            <p>{person.Email || '-'}</p>
                                        </div>
                                    ))
                                : <p>-</p>}
                        </div>
                    </div>
                </div>
                <div className="border rounded-lg p-6 flex-1">
                    <div className="flex flex-row items-center justify-between">
                        <h2 className="text-lg font-semibold">
                            {t(
                                'module-electrical.overview.inspection-company.title',
                            )}
                        </h2>
                        {/* TODO: remove hardcoded image with hardcoded strings */}
                        {/* <img
                            className="h-6 object-fill"
                            src="/logo-expert-inspecties.png"
                        /> */}
                    </div>
                    <div className="mt-4">
                        <p className="text-sm font-semibold mb-1">
                            {t(
                                'module-electrical.overview.inspection-company.declaration',
                            )}
                        </p>
                        <p className="text-sm">
                            <span className={`font-bold ${projectQuery.data?.Project?.IsFinalVerdictPassed ? `text-green-600` : `text-red-600`}`}>
                                {projectQuery.data?.Project?.IsFinalVerdictPassed
                                    ? t('module-electrical.overview.inspection-company.declaration-passed')
                                    : t('module-electrical.overview.inspection-company.declaration-not-passed')}
                            </span>
                            {' '}
                            {!projectQuery.data?.Project?.IsFinalVerdictPassed
                                && t('module-electrical.overview.inspection-company.failed-text')}
                        </p>
                        {!projectQuery.data?.Project?.IsFinalVerdictPassed && (
                            <Button
                                className="border-2 border-primary/30 rounded-lg bg-background-primary text-primary font-semibold mt-2 hover:bg-primary/10"
                                onClick={() => setComingSoonModalOpen(true)}
                            >
                                <RefreshCw className="mr-2" />
                                {' '}
                                {t(
                                    'module-electrical.overview.inspection-company.reinspection-request',
                                )}
                            </Button>
                        )}
                    </div>
                    <Separator className="my-4 bg-background" />
                    <div className="mt-4">
                        <p className="text-sm font-semibold mb-1">
                            {t(
                                'module-electrical.overview.inspection-company.name',
                            )}
                        </p>
                        <p className="text-sm">
                            {projectQuery.data?.Project?.InspectionOrganization?.Name || '-'}
                        </p>
                    </div>
                    <Separator className="my-4 bg-background" />
                    <div>
                        <p className="text-sm font-semibold mb-1">
                            {t(
                                'module-electrical.overview.inspection-company.address',
                            )}
                        </p>
                        <p className="text-sm">
                            {projectQuery.data?.Project?.InspectionOrganization?.Address?.Street || projectQuery.data?.Project?.InspectionOrganization?.Address?.StreetNumber
                                ? `${projectQuery.data?.Project?.InspectionOrganization?.Address?.Street} ${projectQuery.data?.Project?.InspectionOrganization?.Address?.StreetNumber}`
                                : '-'}
                        </p>
                    </div>
                    <Separator className="my-4 bg-background" />
                    <div>
                        <p className="text-sm font-semibold mb-1">
                            {t(
                                'module-electrical.overview.inspection-company.place',
                            )}
                        </p>
                        <p className="text-sm">
                            {projectQuery.data?.Project?.InspectionOrganization?.Address?.PostalCode || projectQuery.data?.Project?.InspectionOrganization?.Address?.Locality
                                ? `${projectQuery.data?.Project?.InspectionOrganization?.Address?.PostalCode} ${projectQuery.data?.Project?.InspectionOrganization?.Address?.Locality}`
                                : '-'}
                        </p>
                    </div>
                    <Separator className="my-4 bg-background" />
                    <div>
                        <p className="text-sm font-semibold mb-1">
                            {t(
                                'module-electrical.overview.inspection-company.email',
                            )}
                        </p>
                        <p className="text-sm">
                            {projectQuery.data?.Project?.InspectionOrganization?.Email || '-'}
                        </p>
                    </div>
                </div>
            </div>
            <div className="mb-10">
                <div className="flex flex-col md:flex-row justify-between border rounded-t-2xl p-4">
                    <div className="flex sm:flex-row flex-col justify-between p-2 w-full">
                        <div>
                            <h2 className="text-xl font-semibold">
                                {t(
                                    'module-electrical.overview.attached-files-table.header.title',
                                )}
                            </h2>
                            <p className="mt-1 text-text-primary/70 text-base">
                                {t(
                                    'module-electrical.overview.attached-files-table.header.subtitle',
                                )}
                            </p>
                        </div>
                        <div className="mt-2 sm:mt-0">
                            <SearchInput route={Route} />
                        </div>
                    </div>
                </div>
                <DocumentsDataTable />
            </div>
            <ComingSoonModal open={comingSoonModalOpen} onOpenChange={setComingSoonModalOpen} />
        </>
    );
}

async function DownloadFile(FileId?: string | null, FileName?: string | null, Type?: ListDocumentsQuery | ProjectTechnicaldrawings) {
    try {
        if ((Type && 'DocumentId' in Type)) {
            const downloadOptions = portaalQueryOptions.electrical.documents.downloadBlob({
                DocumentId: FileId ?? '',
            });

            const response = await queryClient.ensureQueryData(
                downloadOptions,
            );

            const url = window.URL.createObjectURL(response);
            const link = document.createElement('a');
            link.href = url;
            link.download = FileName || 'download';
            document.body.appendChild(link);
            link.click();
            window.URL.revokeObjectURL(url);
            document.body.removeChild(link);
        }

        if ((Type && 'ElectricalProjectFileId' in Type)) {
            const downloadOptions = portaalQueryOptions.files.electricalProject({
                ElectricalProjectFileId: FileId ?? '',
                ImageQuality: 'OriginalWebp',
            });

            const response = await queryClient.ensureQueryData(
                downloadOptions,
            );

            const url = window.URL.createObjectURL(response);
            const link = document.createElement('a');
            link.href = url;
            link.download = FileName || 'download';
            document.body.appendChild(link);
            link.click();
            window.URL.revokeObjectURL(url);
            document.body.removeChild(link);
        }
    }
    catch {
        toast.error('Er is iets misgegaan.');
    }
}

function DocumentsDataTable() {
    const { t } = useTranslation('customer-module-electrical');
    const params = Route.useParams();

    const [selectedDownload, setSelectedDownload] = useState({
        FileId: '',
        isDownloading: false,
    });

    const [expandedId, setExpandedId] = useState<string | null>(null);

    const search = Route.useSearch();

    const documentsQuery = useQuery({
        ...portaalQueryOptions.electrical.documents.list({
            ElectricalProjectId: params.projectId ?? null,
        }),
    });

    const technicaldrawingsQuery = useQuery(portaalQueryOptions.electrical.technicalDrawings.list({
        ElectricalProjectId: params.projectId ?? null,
    }));

    const filteredFiles = useMemo(() => {
        const documents: (ListDocumentsQuery | ProjectTechnicaldrawings)[] = [];

        if (documentsQuery.isSuccess && technicaldrawingsQuery.isSuccess) {
            const allVersions = documentsQuery.data?.Versions ?? [];

            const docContentKinds = ['ElectricalProjectScope8Report', 'ElectricalProjectScope10Report', 'ElectricalProjectScope12Report', 'ElectricalProjectNen1010Report', 'ElectricalProjectNen3140Report'];

            const mostRecentVersion = allVersions.find(version => version?.Documents?.find(doc => docContentKinds.includes(doc?.ContentKind ?? '')));

            if (mostRecentVersion) {
                mostRecentVersion.Documents.forEach((doc) => {
                    documents.push({
                        ...doc,
                        DownloadId: doc.DocumentId,
                    });
                });
            }

            const priceQuoteKind = 'ElectricalProjectPriceQuote';
            for (const version of allVersions) {
                const found = version.Documents.find(doc => doc.ContentKind === priceQuoteKind);
                if (found) {
                    documents.push({
                        ...found,
                        DownloadId: found.DocumentId,
                    });
                    break;
                }
            }

            const inspectionPlanKind = 'ElectricalProjectInspectionPlan';
            for (const version of allVersions) {
                const found = version.Documents.find(doc => doc.ContentKind === inspectionPlanKind);
                if (found) {
                    documents.push({
                        ...found,
                        DownloadId: found.DocumentId,
                    });
                    break;
                }
            }

            technicaldrawingsQuery.data?.TechnicalDrawings?.forEach((row) => {
                documents.push({
                    ...row,
                    DownloadId: row.ElectricalProjectFileId ?? '',
                });
            });
        }

        return documents;
    }, [documentsQuery.data?.Versions, documentsQuery.isSuccess, technicaldrawingsQuery.data?.TechnicalDrawings, technicaldrawingsQuery.isSuccess]);

    const columns = useMemo(() => [
        {
            accessorKey: 'Name',
            header: () => (
                <DataTable.Header>
                    {t('module-electrical.overview.attached-files-table.columns.version-filename')}
                </DataTable.Header>
            ),
            cell: ({ row }: { row: { original: (ListDocumentsQuery | ProjectTechnicaldrawings) } }) => (
                <DataTable.Cell variant="bold">
                    <Button
                        variant="ghost"
                        className="sm:hidden"
                        onClick={() => setExpandedId(prev => prev === row?.original?.DownloadId ? null : row?.original?.DownloadId ?? null)}
                    >
                        {expandedId === row?.original?.DownloadId ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                    </Button>
                    <div className="flex gap-3 items-center">
                        <DataTable.Cell
                            variant="fileIcon"
                            fileType={row?.original?.File?.Name?.split('.').pop()?.toLowerCase() ?? 'unknown'}
                        />
                        <div className="flex flex-col">
                            <div>{row?.original?.File?.Name ?? null}</div>
                            <div className="font-normal text-text-primary/60 mt-1">
                                {formatFileSize(row?.original?.File?.Size)}
                            </div>
                        </div>
                    </div>
                </DataTable.Cell>
            ),
        },
        {
            accessorKey: 'Date',
            meta: {
                mobileHidden: true,
            },
            header: () => (
                <DataTable.Header>
                    {t('module-electrical.overview.attached-files-table.columns.date')}
                </DataTable.Header>
            ),
            cell: ({ row }: { row: { original: ListDocumentsQuery | ProjectTechnicaldrawings } }) => (
                <DataTable.Cell variant="bold">
                    {row?.original?.File?.LastUpdateDate
                        ? new Date(row.original.File.LastUpdateDate).toLocaleDateString('nl-NL', {
                                day: '2-digit',
                                month: '2-digit',
                                year: 'numeric',
                            })
                        : '-'}
                </DataTable.Cell>
            ),
        },
        {
            accessorKey: 'Time',
            meta: {
                mobileHidden: true,
            },
            header: () => (
                <DataTable.Header>
                    {t('module-electrical.overview.attached-files-table.columns.time')}
                </DataTable.Header>
            ),
            cell: ({ row }: { row: { original: ListDocumentsQuery | ProjectTechnicaldrawings } }) => (
                <DataTable.Cell variant="bold">
                    {row?.original?.File?.LastUpdateDate
                        ? new Date(row.original.File.LastUpdateDate).toLocaleTimeString('nl-NL', {
                                hour: '2-digit',
                                minute: '2-digit',
                            })
                        : '-'}
                </DataTable.Cell>
            ),
        },
        {
            accessorKey: 'InspectionKind',
            meta: {
                mobileHidden: true,
            },
            header: () => (
                <DataTable.Header>
                    {t('module-electrical.overview.attached-files-table.columns.type')}
                </DataTable.Header>
            ),
            cell: ({ row }: { row: { original: ListDocumentsQuery | ProjectTechnicaldrawings } }) => {
                let value = '';

                'DocumentId' in row.original
                    ? (row?.original?.ContentKind
                            ? value = t(`module-electrical.overview.attached-files-table.file-types.${row?.original?.ContentKind}`)
                            : value = t('module-electrical.overview.attached-files-table.columns.file'))
                    : t('module-electrical.overview.attached-files-table.columns.file');

                if ('ElectricalProjectTechnicalDrawingId' in row.original) {
                    if (row?.original?.Location) {
                        value = row?.original?.Location;
                    }
                    else {
                        value = t('module-electrical.overview.attached-files-table.columns.file');
                    }
                }
                if ('ElectricalProjectVersionId' in row?.original) {
                    value = t(`module-electrical.overview.attached-files-table.file-types.${row?.original?.ContentKind}`) ?? t('module-electrical.overview.attached-files-table.columns.file');
                }

                return (
                    <DataTable.Cell variant="bold">
                        {value}
                    </DataTable.Cell>
                );
            },
        },
        {
            accessorKey: 'actions',
            meta: {
                sticky: true,
                width: 10,
            },
            header: '',
            cell: ({ row }: { row: { original: (ListDocumentsQuery | ProjectTechnicaldrawings) } }) => {
                return (
                    <div className="flex gap-1 items-center justify-end ml-auto">
                        <Button
                            variant="secondary"
                            size="icon"
                            className="flex gap-2 w-fit h-fit justify-start p-2 bg-background-primary text-foreground hover:bg-background-secondary"
                        >

                            {selectedDownload.FileId === row?.original?.DownloadId && selectedDownload.isDownloading
                                ? <Spinner />
                                : (
                                        <CloudDownload
                                            onClick={() => {
                                                setSelectedDownload({
                                                    FileId: row?.original?.DownloadId ?? '',
                                                    isDownloading: true,
                                                });
                                                DownloadFile(row?.original?.DownloadId, row?.original?.File?.Name, row?.original).then(() => {
                                                    setSelectedDownload({
                                                        FileId: '',
                                                        isDownloading: false,
                                                    });
                                                });
                                            }}
                                            className="text-text-primary/70 size-5"
                                        />
                                    )}
                        </Button>
                    </div>
                );
            },
        },
    ], [expandedId, selectedDownload.FileId, selectedDownload.isDownloading, t]);

    const tableColumns = useMemo(
        () => documentsQuery.isLoading
            ? columns.map(column => ({ ...column, cell: () => <Skeleton className="h-8 w-full" /> }))
            : columns,
        [columns, documentsQuery.isLoading],
    );

    const tableData = useMemo<(ListDocumentsQuery | ProjectTechnicaldrawings)[]>(
        () => {
            if (documentsQuery.isError) {
                toast.error(documentsQuery.error?.Message);
                return [];
            }

            const searchTerm = (search.search ?? '').toLowerCase();

            if (!filteredFiles)
                return [];

            return filteredFiles.filter((row) => {
                if (row?.File && !row.File.FileId) {
                    return false;
                }

                if (!searchTerm)
                    return true;

                const fields: string[] = [];

                fields.push(row.File?.Name ?? '');

                if ('DocumentId' in row) {
                    fields.push(row.ContentKind ?? '');
                }

                if ('Description' in row && typeof row.Description === 'string') {
                    fields.push(row.Description);
                }

                if ('Location' in row && typeof row.Location === 'string') {
                    fields.push(row.Location);
                }

                if (row.File?.LastUpdateDate) {
                    const dateObj = new Date(row.File.LastUpdateDate);
                    fields.push(dateObj.toLocaleDateString('nl-NL', {
                        day: '2-digit',
                        month: '2-digit',
                        year: 'numeric',
                    }));
                    fields.push(dateObj.toLocaleTimeString('nl-NL', {
                        hour: '2-digit',
                        minute: '2-digit',
                    }));
                }

                return fields.some(field => field.toLowerCase().includes(searchTerm));
            }) || [];
        },
        [documentsQuery.isError, documentsQuery.error?.Message, search.search, filteredFiles],
    );

    return (
        <div className="flex flex-col">
            <DataTable
                className="rounded-tl-none rounded-tr-none"
                columns={tableColumns ?? []}
                data={tableData}
                pageSize={100}
                expandedRow={row =>
                    expandedId && expandedId === row?.original?.DownloadId
                        ? (
                                <ExpandedRowsMobile columns={columns} data={row.original} />
                            )
                        : null}
            />
        </div>
    );
}
